import { queryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { createQueryGroup } from "../../core/api/queries";
import {
  PatientDocumentTypeId,
  PatientId,
  PatientTaskInstanceId,
} from "../../shared/schema/schema";

// Prefix shared with the Tasks 2.0 list so the drawer's invalidations refresh the review too
export const DOCUMENT_REVIEW_ROOT_QUERY_KEY = "patientTaskRegulars";

const MAX_RETRIES = 3;
const PDF_GENERATION_POLL_INTERVAL_MS = 3000;

/** A 4xx (no access, not found, bad request) will not change by asking again: fail fast. */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}

export default createQueryGroup(({ api }) => ({
  review: (taskInstanceId: PatientTaskInstanceId, documentTypeId: PatientDocumentTypeId) =>
    queryOptions({
      queryKey: [DOCUMENT_REVIEW_ROOT_QUERY_KEY, "documentReview", taskInstanceId, documentTypeId],
      queryFn: () =>
        api.get(
          "./patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/review",
          { path: { patientTaskInstanceId: taskInstanceId, documentTypeId } }
        ),
      retry: retryUnlessClientError,
      // The PDF is generated in the background (e.g. after a submit): poll until it is ready
      refetchInterval: (query) =>
        query.state.data?.isPdfGenerating === true ? PDF_GENERATION_POLL_INTERVAL_MS : false,
    }),

  /** The patient's contract diagnosis (ICD) codes: what icdCodes / finalIcdCodes print. */
  patientContractDiagnosisCodes: (patientId: PatientId) =>
    queryOptions({
      queryKey: ["patient", patientId, "contractDiagnosisCodes"],
      queryFn: () =>
        api.get("./patients/:patientId/patient_contract_diagnosis_codes", {
          path: { patientId },
        }),
      select: ({ patientContractDiagnosisCodes }) => patientContractDiagnosisCodes,
    }),
}));
